import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { 
  BarChart3, 
  TrendingUp, 
  FileText, 
  Download, 
  Activity,
  Shield,
  RefreshCw
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Navigate } from 'react-router-dom';

// Import existing page components to use as tab content
import AdvancedAnalytics from './AdvancedAnalytics';
import CustomReportBuilder from './CustomReportBuilder';
import PerformanceMetrics from './PerformanceMetrics';
import DataExportTools from './DataExportTools';
import ComplianceReporting from './ComplianceReporting';
import AnalyticsReporting from './AnalyticsReporting';

const UnifiedAnalytics = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialTab = searchParams.get('tab') || 'overview';
  const [activeTab, setActiveTab] = useState(initialTab);

  // Update URL when tab changes
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
  };

  // Sync activeTab with URL params
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab') || 'overview';
    if (tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);

  const tabs = [
    { 
      id: 'overview', 
      label: 'Overview', 
      icon: BarChart3,
      description: 'Advanced analytics and insights'
    },
    { 
      id: 'reports', 
      label: 'Custom Reports', 
      icon: FileText,
      description: 'Build and manage custom reports'
    },
    { 
      id: 'metrics', 
      label: 'Performance Metrics', 
      icon: Activity,
      description: 'System performance metrics'
    },
    { 
      id: 'export', 
      label: 'Data Export', 
      icon: Download,
      description: 'Export analytics data'
    },
    { 
      id: 'compliance', 
      label: 'Compliance', 
      icon: Shield,
      description: 'Compliance reporting'
    }
  ];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <BarChart3 className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Analytics & Reports</h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                Comprehensive analytics and reporting dashboard
              </p>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => navigate('/admin/dashboard')}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </div>

      {/* Tab Navigation */}
      <Card>
        <CardContent className="p-0">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="flex space-x-1 overflow-x-auto px-4" aria-label="Analytics Tabs">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className={`flex items-center space-x-2 py-4 px-4 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                        : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:border-gray-300'
                    }`}
                    title={tab.description}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'overview' && (
              <div>
                <AdvancedAnalytics />
              </div>
            )}

            {activeTab === 'reports' && (
              <div>
                <CustomReportBuilder />
              </div>
            )}

            {activeTab === 'metrics' && (
              <div>
                <PerformanceMetrics />
              </div>
            )}

            {activeTab === 'export' && (
              <div>
                <DataExportTools />
              </div>
            )}

            {activeTab === 'compliance' && (
              <div>
                <ComplianceReporting />
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default UnifiedAnalytics;

